// AnomaliesPanel — Phase 5b-2 anomalies endpoint.
// 시안 design/v3/AnalyticsPanels.jsx:339-372 의 이상치 감지 layout 따름.
// endpoint 의 type 'high'|'low' + zScore 로 severity 결정 (|z| >= 2.5 = high).

import { Card } from '../../primitives';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  SectionHead,
} from '../bits';
import { useAnomalies, type AnalyticsScope } from '../queries';
import { fmtWonShort } from '../../../lib/format';
import type { AnalyticsAnomalyRow } from '../../../types/api';

interface Props {
  scope: AnalyticsScope;
}

function severity(z: number): {
  label: string;
  bg: string;
  fg: string;
} {
  const az = Math.abs(z);
  if (az >= 2.5) {
    return { label: '높음', bg: 'var(--danger-bg)', fg: 'var(--danger)' };
  }
  if (az >= 2.0) {
    return { label: '중간', bg: '#FEF3C7', fg: '#78350F' };
  }
  return { label: '낮음', bg: 'var(--bg-muted)', fg: 'var(--fg-2)' };
}

export function AnomaliesPanel({ scope }: Props) {
  const q = useAnomalies(scope);
  const items = q.data ?? [];

  return (
    <Card style={{ padding: 20 }}>
      <SectionHead
        title="이상치 감지"
        sub={items.length ? `${items.length}건 · z-score 1.5 이상` : '없음'}
      />
      {q.isLoading && <LoadingState />}
      {q.isError && <ErrorState message={(q.error as Error).message} />}
      {q.data && items.length === 0 && (
        <EmptyState
          title="이상치가 감지되지 않았습니다"
          message="기간 내 매출이 평균에서 크게 벗어난 날이 없습니다."
        />
      )}
      {items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((a: AnalyticsAnomalyRow) => {
            const s = severity(a.zScore);
            const kind = a.type === 'high' ? '급증' : '급감';
            return (
              <div
                key={a.date}
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: 'var(--bg-subtle)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                }}
              >
                <span
                  style={{
                    padding: '2px 7px',
                    borderRadius: 4,
                    background: s.bg,
                    color: s.fg,
                    fontSize: 11,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  {kind}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: 'var(--fg-1)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {a.date}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--fg-3)',
                      marginTop: 2,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    매출 {fmtWonShort(a.revenue)} · z={a.zScore.toFixed(2)}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color:
                        a.type === 'high'
                          ? 'var(--success)'
                          : 'var(--danger)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {a.type === 'high' ? '+' : ''}
                    {a.zScore.toFixed(1)}σ
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--fg-3)' }}>
                    {s.label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
